import React, { useState, useEffect } from 'react';
import { getProzesse, getAnalysen, createAnalyse, updateAnalyse } from '../../../../../services/schutzbedarfsanalyseService';
import AnalyseModal from './AnalyseModal';
import './AnalysenSection.css';

const AnalysenSection = ({ onAnalyseSelect, selectedAnalyse }) => {
    const [prozesse, setProzesse] = useState([]);
    const [analysen, setAnalysen] = useState([]);
    const [selectedProzess, setSelectedProzess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingAnalyse, setEditingAnalyse] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        const [prozesseResult, analysenResult] = await Promise.all([
            getProzesse(),
            getAnalysen()
        ]);

        if (prozesseResult.success) {
            setProzesse(prozesseResult.data);
        } else {
            setError(prozesseResult.error);
        }

        if (analysenResult.success) {
            setAnalysen(analysenResult.data);
        }

        setLoading(false);
    };

    const handleProzessSelect = (prozess) => {
        setSelectedProzess(prozess);
    };

    const handleCreateAnalyse = () => {
        if (!selectedProzess) {
            alert('Bitte wählen Sie zuerst einen Prozess aus');
            return;
        }
        setEditingAnalyse(null);
        setShowModal(true);
    };

    const handleEditAnalyse = (analyse) => {
        const prozess = prozesse.find(p => p.prozess_id === analyse.prozess_id);
        setSelectedProzess(prozess);
        setEditingAnalyse(analyse);
        onAnalyseSelect(analyse); // Setze ausgewählte Analyse
        setShowModal(true);
    };

    const handleAnalyseClick = (analyse) => {
        // Nur Selection setzen, KEIN Modal öffnen
        onAnalyseSelect(analyse);
    };

    const handleSave = async (analyseData) => {
        let result;

        if (editingAnalyse) {
            result = await updateAnalyse(editingAnalyse.analyse_id, analyseData);
        } else {
            result = await createAnalyse(analyseData);
        }

        if (result.success) {
            setShowModal(false);
            setEditingAnalyse(null);
            await loadData();

            // Setze die neu erstellte/aktualisierte Analyse als ausgewählt
            if (result.data) {
                onAnalyseSelect(result.data);
            }
        } else {
            alert(result.error);
        }
    };

    const getSchutzbedarfBadge = (level) => {
        const colors = {
            'sehr_hoch': '#ef4444',
            'hoch': '#f59e0b',
            'normal': '#22c55e',
            'gering': '#10b981',
        };

        const labels = {
            'sehr_hoch': 'Sehr Hoch',
            'hoch': 'Hoch',
            'normal': 'Normal',
            'gering': 'Gering',
        };

        return (
            <span
                className="schutzbedarf-badge"
                style={{
                    background: colors[level] || '#666',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                }}
            >
                {labels[level] || 'Nicht bewertet'}
            </span>
        );
    };

    const getStatusBadge = (status) => {
        const colors = {
            'entwurf': '#6b7280',
            'in_bearbeitung': '#3b82f6',
            'abgeschlossen': '#22c55e',
        };

        const labels = {
            'entwurf': 'Entwurf',
            'in_bearbeitung': 'In Bearbeitung',
            'abgeschlossen': 'Abgeschlossen',
        };

        return (
            <span
                className="status-badge"
                style={{
                    background: colors[status] || '#666',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '11px',
                    fontWeight: '600'
                }}
            >
                {labels[status] || status}
            </span>
        );
    };

    const getProzessAnalysen = (prozessId) => {
        return analysen.filter(a => a.prozess_id === prozessId);
    };

    if (loading) {
        return (
            <div className="analysen-section">
                <div className="loading-state">Lade Analysen...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="analysen-section">
                <div className="error-state">
                    <p>Fehler beim Laden: {error}</p>
                    <button onClick={loadData} className="btn-retry">Erneut versuchen</button>
                </div>
            </div>
        );
    }

    return (
        <div className="analysen-section">
            <div className="section-header">
                <div>
                    <h2>Schutzbedarfsanalysen</h2>
                    <p className="section-subtitle">BSI IT-Grundschutz Schutzziel-Bewertung</p>
                    <br />
                </div>
                <button onClick={handleCreateAnalyse} className="btn-primary">
                    Neue Analyse
                </button>
            </div>

            {prozesse.length === 0 ? (
                <div className="empty-state">
                    <h3>Keine Prozesse vorhanden</h3>
                    <p>Erstellen Sie zuerst Geschäftsprozesse im Prozesse-Tab</p>
                </div>
            ) : (
                <div className="analysen-layout">
                    <div className="prozesse-list">
                        <h3 className="list-title">Prozesse</h3>
                        {prozesse.map((prozess) => {
                            const prozessAnalysen = getProzessAnalysen(prozess.prozess_id);
                            const isSelected = selectedProzess?.prozess_id === prozess.prozess_id;

                            return (
                                <div
                                    key={prozess.prozess_id}
                                    className={`prozess-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleProzessSelect(prozess)}
                                >
                                    <div className="prozess-item-header">
                                        <h4>{prozess.prozess_name}</h4>
                                        {prozess.gesamtschutzbedarf && getSchutzbedarfBadge(prozess.gesamtschutzbedarf)}
                                    </div>
                                    <p className="prozess-item-meta">
                                        {prozessAnalysen.length} Analyse{prozessAnalysen.length !== 1 ? 'n' : ''}
                                    </p>
                                </div>
                            );
                        })}
                    </div>

                    <div className="analysen-content">
                        {selectedProzess ? (
                            <>
                                <div className="content-header">
                                    <h3>{selectedProzess.prozess_name}</h3>
                                    <button onClick={handleCreateAnalyse} className="btn-secondary btn-small">
                                        Analyse erstellen
                                    </button>
                                </div>

                                {getProzessAnalysen(selectedProzess.prozess_id).length === 0 ? (
                                    <div className="empty-state-small">
                                        <p>Keine Analysen für diesen Prozess vorhanden</p>
                                        <button onClick={handleCreateAnalyse} className="btn-primary btn-small">
                                            Erste Analyse erstellen
                                        </button>
                                    </div>
                                ) : (
                                    <div className="analysen-grid">
                                        {getProzessAnalysen(selectedProzess.prozess_id).map((analyse) => {
                                            // Check if THIS analyse is selected
                                            const isAnalyseSelected = selectedAnalyse?.analyse_id === analyse.analyse_id;

                                            return (
                                                <div
                                                    key={analyse.analyse_id}
                                                    className={`analyse-card ${isAnalyseSelected ? 'analyse-card-selected' : ''}`}
                                                    onClick={() => handleAnalyseClick(analyse)}
                                                >
                                                    <div className="analyse-card-header">
                                                        <div>
                                                            {getSchutzbedarfBadge(analyse.gesamtschutzbedarf)}
                                                            {getStatusBadge(analyse.status)}
                                                        </div>
                                                        <span className="analyse-date">
                                                            {new Date(analyse.analyse_datum).toLocaleDateString('de-DE')}
                                                        </span>
                                                    </div>

                                                    <div className="schutzziele-grid">
                                                        <div className="schutzziel-item">
                                                            <span className="schutzziel-label">Vertraulichkeit</span>
                                                            <span className="schutzziel-value">{analyse.vertraulichkeit}</span>
                                                        </div>
                                                        <div className="schutzziel-item">
                                                            <span className="schutzziel-label">Integrität</span>
                                                            <span className="schutzziel-value">{analyse.integritaet}</span>
                                                        </div>
                                                        <div className="schutzziel-item">
                                                            <span className="schutzziel-label">Verfügbarkeit</span>
                                                            <span className="schutzziel-value">{analyse.verfuegbarkeit}</span>
                                                        </div>
                                                        <div className="schutzziel-item">
                                                            <span className="schutzziel-label">Authentizität</span>
                                                            <span className="schutzziel-value">{analyse.authentizitaet || 'Normal'}</span>
                                                        </div>
                                                    </div>

                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation(); // Verhindere doppelten Click
                                                            handleEditAnalyse(analyse);
                                                        }}
                                                        className="btn-ghost btn-small full-width"
                                                    >
                                                        Details anzeigen
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="no-selection">
                                <p>Wählen Sie einen Prozess aus der Liste</p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showModal && (
                <AnalyseModal
                    prozess={selectedProzess}
                    analyse={editingAnalyse}
                    onSave={handleSave}
                    onCancel={() => {
                        setShowModal(false);
                        setEditingAnalyse(null);
                    }}
                />
            )}
        </div>
    );
};

export default AnalysenSection;